import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LayoutDashboard, Users, ClipboardList, LogOut, Menu, X, ShieldCheck, BookOpen, Settings, Clock } from 'lucide-react';
import { Button, Modal } from '../ui/UI';
import './AdminLayout.css';

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);

  const updateStatusKey = admin ? `hse_update_modal_shown_${admin._id || admin.email}` : null;

  useEffect(() => {
    if (!admin) return;
    const shown = localStorage.getItem(updateStatusKey);
    if (!shown) {
      setUpdateModalOpen(true);
    }
  }, [admin, updateStatusKey]);

  const handleLogout = () => {
    setLogoutConfirm(true);
  };

  const confirmLogout = () => {
    logout();
    setLogoutConfirm(false);
    navigate('/login');
  };

  const handleCloseUpdateModal = () => {
    if (updateStatusKey) {
      localStorage.setItem(updateStatusKey, 'true');
    }
    setUpdateModalOpen(false);
  };

  const navGroups = [
    {
      label: 'Operations',
      items: [
        { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
        { to: '/admin/observations', label: 'Submitted Observations', icon: ClipboardList },
        { to: '/admin/register', label: 'Observation Register', icon: BookOpen },
        { to: '/admin/closed-actions', label: 'Closed Actions', icon: Clock }
      ]
    },
    {
      label: 'Management',
      items: [
        { to: '/admin/observers', label: 'Add New Observers', icon: Users },
        { to: '/admin/form-fields', label: 'Form Dropdowns', icon: Settings }
      ]
    },
    {
      label: 'Admin',
      items: admin?.role === 'superadmin'
        ? [{ to: '/admin/admin-logins', label: 'Admin Management', icon: Clock }]
        : []
    }
  ];

  return (
    <div className="admin-shell">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <ShieldCheck size={18} strokeWidth={2} />
            <span>UFL BBS & VFL</span>
          </div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>
            <X size={16} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navGroups.map((group) => (
            group.items.length > 0 ? <div key={group.label} className="nav-group">
              <div className="nav-group-label">{group.label}</div>
              {group.items.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) => `nav-item ${isActive ? 'nav-item--active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon size={15} strokeWidth={1.75} />
                  <span>{label}</span>
                </NavLink>
              ))}
            </div> : null
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">{admin?.fullName?.[0]?.toUpperCase() || 'A'}</div>
            <div className="user-info">
              <span className="user-name">{admin?.fullName}</span>
              <span className="user-role text-xs text-muted">{admin?.role === 'superadmin' ? 'Superadmin' : 'Administrator'}</span>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout} title="Logout">
            <LogOut size={14} />
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="admin-main">
        <header className="admin-topbar">
          <button className="menu-toggle" onClick={() => setSidebarOpen(true)}>
            <Menu size={18} />
          </button>
          <div className="topbar-brand">
            <ShieldCheck size={15} strokeWidth={2} />
            <span className="font-semibold">UFL HSE</span>
          </div>
        </header>
        <main className="admin-content">
          <Outlet />
        </main>
      </div>

      {/* Update Modal */}
      <Modal open={updateModalOpen} onClose={handleCloseUpdateModal} title={`Welcome back${admin?.fullName ? `, ${admin.fullName}` : ''}!`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: '0.93rem', color: '#374151' }}>
          <p><strong>We have a new update.</strong></p>
          <p>The register now starts on the selected week, so you can review one week of observations at a time.</p>
          <p>Your Business Unit selection also stays saved until you change it.</p>
          <p>When you close an action from the register, it is moved out of the active register and into the Closed Actions page for follow-up tracking.</p>
          <p>This saves you from reapplying the same filter again and again and helps keep completed actions separated cleanly.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="primary" onClick={handleCloseUpdateModal}>Got it</Button>
          </div>
        </div>
      </Modal>

      {/* Logout Confirmation Modal */}
      {logoutConfirm && createPortal(
        <div className="modal-backdrop" onClick={() => setLogoutConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Confirm Logout</span>
              <button className="modal-close" onClick={() => setLogoutConfirm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.85rem', color: '#4b5563', marginBottom: 16 }}>
                Are you sure you want to logout? You'll be redirected to the login page.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button variant="secondary" onClick={() => setLogoutConfirm(false)}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={confirmLogout}>
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
